import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Button, Space, Spin, Checkbox, Modal, Tooltip, message as $message } from 'antd'
import styles from './index.less'
import CustomIcon from '@/components/CustomIcon'
import classNames from 'classnames'
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc'
import { useGenConfigFields } from './hooks'
import arrayMove from 'array-move'
import { queryUserConfig, addUserConfig, getSearchItem } from '@/services/user'
import isEmpty from 'lodash/isEmpty'
import { createAPI } from '@/utils/modal'
import { getDefaultConfig, formatQuickSortData } from '../../helper'

const Group = ({ children }) => {
    return <div className={styles.group}>{children}</div>
}

const SortableContainer = sortableContainer(Group)

const Drag = sortableHandle(() => {
    return (
        <Tooltip overlay={null} title="拖拽排序">
            <div className={styles.drag}>
                <CustomIcon type="icon-a-Group1000008508" className={styles.action} />
            </div>
        </Tooltip>
    )
})

const Item = ({ className, dataSource, onCheck, ...restProps }) => {
    return (
        <div className={classNames(styles.item, className)} {...restProps}>
            <Checkbox
                className={styles.title}
                checked={dataSource.show}
                disabled={dataSource.disabled}
                value={dataSource.key}
                onChange={({ target: { checked } }) => onCheck(dataSource, checked)}>
                <Tooltip overlay={null} title={dataSource.fieldName}>
                    <div className={classNames('ellipsis', styles.fieldName)}>{dataSource.fieldName}</div>
                </Tooltip>
            </Checkbox>
            {dataSource.enable && <Drag />}
        </div>
    )
}

const SortableElement = sortableElement(Item)

const ResetModal = ({ visible, onCancel, onOk }) => {
    const [loading, setLoading] = useState(false)
    const handleOk = async () => {
        setLoading(true)
        try {
            await onOk()
        } catch (error) {
            console.error('catchError', error)
        }
        onCancel && onCancel()
        setLoading(false)
    }

    return (
        <Modal
            title="提示"
            width={480}
            className={styles.resetModal}
            okText="确认"
            onOk={handleOk}
            okButtonProps={{ loading }}
            visible={visible}
            onCancel={onCancel}>
            恢复默认设置，将按照系统推荐的顺序排列展示。确定要恢复默认设置吗？
        </Modal>
    )
}

export default ({
    fields,
    configKey,
    onSaveCallback,
    onClose,
    isNewUser,
    groupFields,
    onCollapse,
    hideShowQuickSort
}) => {
    const [fieldConfig, setConfig] = useState(null)
    const [initCheckStatus, setInitCheckStatus] = useState(false)
    const [loading, setLoading] = useState(false)
    const filterHiddenElFields = useMemo(() => fields?.filter(item => !item?.hidden), [fields])
    const memoData = useGenConfigFields(filterHiddenElFields, fieldConfig, initCheckStatus)
    const [renderData, setRenderData] = useState(memoData)
    const [showQuickSort, setShowQuickSort] = useState(false)
    const tempQuickSort = useRef(false)

    useEffect(() => {
        setRenderData(memoData)
    }, [memoData])

    useEffect(() => {
        setLoading(true)
        queryUserConfig({ configKey })
            .then(response => {
                setInitCheckStatus(true)
                if (response?.success && response?.data) {
                    setConfig(response.data[configKey])
                }
                // 调试用的
                // if (response?.success) {
                //     setConfig(null)
                // }
            })
            .finally(() => setLoading(false))
        if (!isNewUser) {
            queryUserConfig({ configKey: `${configKey}_DID_QUICK_SORT` }).then(response => {
                const { success, data } = response ?? {}
                if (success) {
                    setShowQuickSort(
                        !(data && data[`${configKey}_DID_QUICK_SORT`] && data[`${configKey}_DID_QUICK_SORT`].value)
                    )
                }
            })
        }
    }, [])

    const handleCheck = (dataSource, checked) => {
        const { config, fieldItems } = renderData
        const temp = { ...config }

        const target = [...fieldItems].map(item => {
            if (dataSource.key === item.key) {
                temp[item.key].show = checked
                return { ...item, show: checked }
            }
            return { ...item }
        })
        setRenderData({
            config: temp,
            fieldItems: target
        })
    }

    const handleSort = ({ oldIndex, newIndex }) => {
        const { config } = renderData
        const temp = { ...config }
        const target = arrayMove(renderData.fieldItems, oldIndex, newIndex)
        target.forEach((item, index) => {
            temp[item.key].sort = index
        })

        setRenderData({
            config: temp,
            fieldItems: target
        })
    }
    const update = async data => {
        setLoading(true)
        if (tempQuickSort.current) {
            try {
                addUserConfig({
                    configKey: `${configKey}_DID_QUICK_SORT`,
                    configValue: { value: true }
                })
            } catch (error) {
                console.error('catchError', error)
            }
        }
        const { success, message } = await addUserConfig({
            configKey,
            configValue: data
        })
        setLoading(false)
        if (success) {
            $message.success('更新成功')
            onClose && onClose()
            onSaveCallback(data)
        } else {
            message && $message.error(message)
        }
        if (tempQuickSort.current) {
            onCollapse && onCollapse()
        }
    }
    const onSaveClk = () => {
        update(renderData.config)
    }
    const onResetClk = () => {
        createAPI(ResetModal, {
            onOk: async () => {
                await update(getDefaultConfig(fields, true))
                onCollapse && onCollapse()
            }
        })
    }

    const onAllClk = ({ target: { checked } }) => {
        const { config } = renderData
        const temp = { ...config }
        Object.keys(temp).forEach(key => {
            temp[key].show = checked
        })
        setConfig(temp)
    }

    const handleQuickSort = () => {
        tempQuickSort.current = true
        setLoading(true)
        getSearchItem({
            configKey
        })
            .then(response => {
                const { success, message, data } = response ?? {}
                if (!success) {
                    $message.error(message)
                    return
                }
                $message.info('筛选项已重新排序，保存后立即生效')
                if (!data || !Object.keys(data).length) {
                    // 保存后没有值从接口返回，则以本地默认规则为主
                    const defaultConfig = getDefaultConfig(fields)
                    setConfig(formatQuickSortData(defaultConfig, fields, renderData.config, groupFields))
                } else {
                    setConfig(formatQuickSortData(data, fields, renderData.config, groupFields))
                }
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const checkedObj = useMemo(() => {
        const hiddenItem = renderData?.fieldItems?.filter(item => !item.show)
        return {
            indeterminate: !isEmpty(hiddenItem) && hiddenItem?.length !== renderData?.fieldItems?.length,
            checked: isEmpty(hiddenItem)
        }
    }, [renderData])

    return (
        <div className={styles.container}>
            <Spin size="small" spinning={loading} wrapperClassName="custom-spin-container">
                {!hideShowQuickSort && showQuickSort && (
                    <div className={styles.alert}>
                        <div className={styles.alertTxt}>
                            <CustomIcon className={styles.alertIcon} type="icon-jiqiao1" />
                            排序建议方案：系统根据您近6个月搜索条件使用的情况，按使用次数由高到低展示
                        </div>
                        <Button className={styles.alertBtn} type="primary" onClick={handleQuickSort}>
                            立即使用
                        </Button>
                    </div>
                )}
                <SortableContainer helperClass={styles.sortHelper} lockAxis="y" useDragHandle onSortEnd={handleSort}>
                    {renderData.fieldItems.map((item, index) => (
                        <SortableElement key={item.key} index={index} dataSource={item} onCheck={handleCheck} />
                    ))}
                </SortableContainer>
                <div className="relative">
                    <div className={styles.footerWrapper}>
                        <Space className={styles.footer}>
                            <div>
                                <Checkbox
                                    indeterminate={checkedObj?.indeterminate}
                                    checked={checkedObj?.checked}
                                    onChange={onAllClk}>
                                    全选
                                </Checkbox>
                            </div>
                            <Space>
                                <Button onClick={onResetClk}>恢复默认</Button>
                                <Button type="primary" onClick={onSaveClk}>
                                    保存
                                </Button>
                            </Space>
                        </Space>
                    </div>
                </div>
            </Spin>
        </div>
    )
}
